import type { JSX, Signal } from '@builder.io/qwik';
import {
  component$,
  useContext,
  useContextProvider,
  useVisibleTask$,
} from '@builder.io/qwik';
import type { ArticleInterface } from '~/types/AppData/PageContent/ArticleInterface';
import ArticleBuilder from '~/components/Layout/Builders/ArticleBuilder';
import FiniteScroll from '~/components/Layout/Body/FiniteScroll';
import { AppDataContext } from '~/contexts/AppDataContext';
import { getTemplate } from '~/utilities/getTemplate';
import { ArticleContext } from '~/contexts/ArticleContext';
import { FINITE_SCROLL_NUM } from '~/constants/FINITE_SCROLL';
import { GA } from '~/utilities/GA';
import { isServer } from '@builder.io/qwik/build';
import { pageBuilderTemplate } from '~/constants/pageBuilderTemplate';
import PageBuilder from '~/components/Layout/Builders/PageBuilder';
import ScoreTicker from '~/components/Shared/Sportradar/ScoreTicker';
import { getHeaderHeight } from '~/utilities/getHeaderHeight';
import { cva } from 'class-variance-authority';
import { useIsSI } from '~/utilities/useIsSI';

type BodyType = {
  finiteScrollIndex: number;
  article?: ArticleInterface;
  finiteScrollStarts: Signal;
};

const containerCSS = cva('', {
  variants: {
    isSIHome: {
      true: 'mt-[5px] lg:mt-0',
    },
  },
});

export default component$<BodyType>(
  ({ finiteScrollIndex, article, finiteScrollStarts }): JSX.Element => {
    const appData = useContext(AppDataContext);
    const isPageBuilder = appData.payloadToSSR.template === pageBuilderTemplate;
    const isSI = useIsSI();

    // TODO: this condition should come from the payload, not from it being property si
    const isSIHome =
      appData.property === 'si' &&
      appData.payloadToSSR.canonicalURL === 'https://www.si.com/';

    const {
      themeComponents,
      payloadToSSR: { layout },
    } = appData;

    useContextProvider(ArticleContext, {
      ...article,
      finiteScrollNumber: finiteScrollIndex,
    });

    const isSwimsuit = appData.property === 'si_swimsuit';
    const headerHeight = getHeaderHeight(themeComponents, layout, {
      isSI,
      // TODO: Remove this once SI sites have a value for firstRowHeight (swimsuit already includes the third header height)
      add: !isSwimsuit && layout.thirdHeader ? 48 : 0,
    });

    // Load on page load
    // eslint-disable-next-line qwik/no-use-visible-task
    useVisibleTask$(() => {
      if (isServer) {
        return;
      }

      const GoogleAnalytics = new GA(article, appData.payloadToSSR, {
        isFiniteScroll: finiteScrollIndex > 0,
      });

      if (finiteScrollIndex > 0) {
        GoogleAnalytics.pushPageViewEvent({
          event: 'GTM data ready to GA finite scroll',
          eventLabel: 'page view',
          eventAction: 'Pageview',
          isSlideshow: false,
          finiteScrollIndex: finiteScrollIndex,
        });
      } else if (appData.payloadToSSR.pageContent.slideshow?.currentSlide) {
        GoogleAnalytics.pushPageViewEvent({
          event: 'GTM data ready to GA - Slideshow',
          eventLabel: 'Slideshow page view',
          eventAction: 'Slideshow Pageview',
          isSlideshow: true,
          finiteScrollIndex: finiteScrollIndex,
          moreProperties: {
            slideNumber:
              appData.payloadToSSR.pageContent.slideshow.currentSlide,
          },
        });
      } else {
        GoogleAnalytics.pushPageViewEvent({
          event: 'GTM data ready to GA',
          eventLabel: 'page view',
          eventAction: 'Pageview',
          isSlideshow: false,
          finiteScrollIndex: finiteScrollIndex,
        });
      }
    });

    return (
      <>
        {isPageBuilder && appData.payloadToSSR.pageBuilderContent && (
          <div
            style={{
              marginTop: finiteScrollIndex === 0 ? headerHeight : 0,

              // Prevent margin collapsing
              paddingTop: '1px',
            }}
          >
            <div
              class={containerCSS({
                isSIHome,
              })}
            >
              {isSIHome && <ScoreTicker />}
              <PageBuilder items={appData.payloadToSSR.pageBuilderContent} />
            </div>
          </div>
        )}

        {!isPageBuilder && (
          <ArticleBuilder
            items={getTemplate(appData)}
            experiments={appData.payloadToSSR.experiments}
          />
        )}

        {/* Stop loading the FiniteScroll component once we've reached NUM_OF_ARTICLES */}
        {!isPageBuilder && finiteScrollIndex < FINITE_SCROLL_NUM - 1 && (
          <FiniteScroll
            finiteScrollIndex={finiteScrollIndex}
            finiteScrollStarts={finiteScrollStarts}
          />
        )}
      </>
    );
  }
);
